// The 牌桌 (card tables: the office runs them, src/office/tables/cards.ts, rules in src/cards/). Shared by every card
// game: near a table (the open deck's hearing distance) a panel shows it, with the game's own part in the middle
// (public/cards-<kind>.js, registered in GAMES below). Around it this file does what every game needs: the title and
// 收起, which game (a picker while nobody else sits there), the status line (whose turn, the clock, the next round),
// sitting down (click the table on the map, a free seat, or 坐下: walk up to that seat and sit) and 离开座位, two-step
// confirmations, and the map's view of every table (deck/cardtables-view.js). The office sends me only what I may see.
//
// A game module's default export: { kind, name, icon, seats, blurb, mount(root, api) → { render(state), tick(),
// status(state) → string | null, leaveWarn(state) → string | null } }; api: send(move) (a ct_act), mySeat(), myTurn(),
// deadline(), name(seat), sitAt(seat), twoStep(key, fn) → a click handler that asks first, confirming(key), toast(text).
import { cardSeat, cardTableDist, nearestCardSeat } from '/cardseats.js';
import { cardsReady, el, seatName } from '/cards-common.js';
import { t, has, onLang, errorText } from '/i18n.js';
import { put, label } from '/icons.js';
import holdem from '/cards-holdem.js';
import mahjong from '/cards-mahjong.js';
import doudizhu from '/cards-doudizhu.js';
import { recordsButton } from '/records.js';
const CSS_URL = new URL('./cards.css', import.meta.url).pathname; // its URL: Vite names the built file by its content (vite.config.mjs)
if (!document.querySelector(`link[href="${CSS_URL}"]`)) {
  const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = CSS_URL; document.head.appendChild(l);
}

/** The games a table can be set up for, in the picker's order (the office's list: src/cards/index.ts). */
const GAMES = [holdem, doudizhu, mahjong];
const SIT_REACH = 1.9; // tiles from my seat's spot to sit (the office allows 2.5)
/** The panel head's buttons: state -> text key (the code says only which state). */
const HEAD = {
  fold: { open: 'cards.fold.hide', folded: 'cards.fold.show' },
  full: { small: 'cards.full.on', full: 'cards.full.off' },
};
/** A game's name with its options' label (麻将 · 推倒胡), worded here (as the office's kindName: src/cards/table.ts);
 *  a game this page doesn't have: the office's words. */
/** A ct_err in words: a game's refusal from its code and params (a kind of play, params.type, in words too), else the
 *  office's text. */
function refusalText(m) {
  if (!m.code) return m.text;
  const params = { ...m.params };
  if (params.type != null && has(`ddz.type.${params.type}`)) params.type = t(`ddz.type.${params.type}`);
  return errorText('cards.err', { code: m.code, params, message: m.text });
}
function kindLabel(kind, opts, fallback) {
  const def = GAMES.find((g) => g.kind === kind); if (!def) return fallback ?? '';
  const l = def.label?.(opts ?? {});
  return l ? t('cards.kind.opt', { game: def.name, opt: l }) : def.name;
}

export function initCards({ send, me, MAP, mover, toast }) {
  const tables = MAP.cardTables || [];
  const myId = () => me()?.gid ?? me()?.id;
  /** The table I'm at (its last state from the office: my own view), the tables' summaries, a sit waiting for me to get there. */
  let cur = null, deadline = 0, nextAt = 0, pending = null, folded = false, confirm = null, pickSel = null;
  /** The options picked at an empty table before sitting (麻将's 玩法: {rules}), sent with my 坐下. */
  let pickOpts = null;
  const summary = new Map();

  // ---- the panel
  const panel = el('div', 'gmpanel ctpanel'); panel.id = 'ctpanel'; panel.style.display = 'none';
  const head = el('div', 'gmhead'), title = el('b', 'gmtitle'), role = el('span', 'gmrole'), fold = el('button', 'gmfold');
  // 玩法: the rules of the table's game as set up (a module's rules(opts)), before and during a game
  const rulesBtn = el('button', 'gmfold ctrulesbtn', t('cards.rules.button'));
  rulesBtn.onclick = () => { const d = shownDef(); if (d) showRules(d, shownOpts()); };
  // 全屏: the panel fills the screen (the map, voice and everything else go on underneath); 缩小 or Esc goes back to
  // the small panel, which is the default. Remembered on this device.
  const fullBtn = el('button', 'gmfold ctfullbtn');
  const FULL_KEY = 'deck.cards.full';
  let full = (() => { try { return localStorage.getItem(FULL_KEY) === '1'; } catch { return false; } })();
  function setFull(on) { full = on; try { localStorage.setItem(FULL_KEY, on ? '1' : '0'); } catch {} if (on) folded = false; render(); }
  fullBtn.onclick = () => setFull(!full);
  // first in line (capture): while the panel fills the screen, Esc only shrinks it (what's under it can't be seen anyway)
  addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !full || panel.style.display === 'none' || e.defaultPrevented || document.querySelector('.ctrules:not([style*="none"])')) return;
    // typing (chat), or a menu the bar opened over the panel: that one's Esc
    const open = (q) => [...document.querySelectorAll(q)].some((x) => getComputedStyle(x).display !== 'none');
    if (e.target?.closest?.('input, textarea, select, [contenteditable]') || open('#emoteMenu, #statusMenu, .dd-list')) return;
    e.preventDefault(); e.stopImmediatePropagation(); setFull(false);
  }, true);
  head.append(title, role, rulesBtn, fullBtn, fold);
  const pick = el('div', 'gmpick'), pickLabel = el('span', 'gmpicklabel', t('cards.pick.label'));
  pick.append(pickLabel, ...GAMES.map((k) => { const b = label(el('button'), k.icon, k.name); b.dataset.kind = k.kind; b.onclick = () => choose(k.kind); return b; }));
  // the game's options (a module's options: [{key, label, choices: [{id, name, blurb}]}]), shown where it has a choice
  const optRow = el('div', 'gmpick ctopts');
  const status = el('div', 'gmstatus ctstatus');
  const area = el('div', 'ctarea');
  const btns = el('div', 'gmbtns'), leave = el('button'), sitBtn = el('button', 'gmprimary', t('cards.sit'));
  btns.append(sitBtn, leave, recordsButton(() => cur?.kind ?? 'holdem', '')); // 战绩 / 排行榜 (public/records.js)
  const body = el('div', 'gmbody'); body.append(pick, optRow, status, area, btns);
  panel.append(head, body);
  document.body.appendChild(panel);
  fold.onclick = () => { folded = !folded; render(); };
  const twoStep = (key, fn) => () => {
    if (confirm?.key === key && Date.now() < confirm.until) { confirm = null; fn(); }
    else confirm = { key, until: Date.now() + 3000 };
    render();
  };
  const confirming = (key) => confirm?.key === key && Date.now() < confirm.until;
  leave.onclick = () => (game()?.ui.leaveWarn?.(cur) ? twoStep('leave', stand)() : stand());
  sitBtn.onclick = () => sitDown(cur?.table);
  function stand() { send({ t: 'ct_stand', table: cur.table }); }
  function choose(kind) {
    if (mySeat() >= 0) send({ t: 'ct_game', table: cur.table, game: kind });
    else { pickSel = kind; pickOpts = null; render(); }
  }
  /** A pick at an empty table is a local preview until the office accepts the seat. Once anybody sits or a round
   *  starts, the authoritative table state takes over. */
  const previewing = () => !!cur && cur.phase !== 'playing' && !cur.seats.some(Boolean) && !!(pickSel || pickOpts);
  const shownDef = () => (cur ? GAMES.find((g) => g.kind === (previewing() ? pickSel ?? cur.kind : cur.kind)) : null);
  const shownOpts = () => (previewing() ? { ...(pickSel && pickSel !== cur?.kind ? {} : cur?.opts ?? {}), ...(pickOpts ?? {}) } : cur?.opts ?? {});
  const shownState = () => {
    if (!cur || !previewing()) return cur;
    const def = shownDef(); if (!def) return cur;
    // A different game's old server view cannot describe the preview. Do not carry cards, results or seat counts
    // across games; the selected game and options are sent with ct_sit and become real only after ct_state arrives.
    return { ...cur, kind: def.kind, opts: shownOpts(), phase: 'waiting', seats: Array(def.seats).fill(null),
      away: Array(def.seats).fill(false), actors: [], legal: [], view: {}, left: 0, nextIn: 0 };
  };
  function chooseOpt(key, id) {
    const d = shownDef(); if (!d) return;
    const opts = { ...shownOpts(), [key]: id };
    if (mySeat() >= 0) send({ t: 'ct_game', table: cur.table, game: d.kind, opts });
    else { pickOpts = opts; render(); }
  }

  // ---- 玩法: one card for every game's rules (the panel's 玩法, the map card's 看玩法)
  const rulesCard = el('div', 'gmpanel ctrules'); rulesCard.style.display = 'none';
  document.body.appendChild(rulesCard);
  let rulesShown = null; // {def, opts}: drawn again on a language change while open
  function showRules(def, opts) {
    const r = def.rules?.(opts ?? {}); if (!r) return;
    rulesShown = { def, opts };
    const close = el('button', 'gmfold', t('cards.rules.close')); close.onclick = () => { rulesCard.style.display = 'none'; };
    const h = el('div', 'gmhead'); h.append(el('b', 'gmtitle', t('cards.rules.title', { game: def.name, rules: r.title ?? '' })), close);
    const out = [h];
    for (const sec of r.sections ?? []) {
      out.push(el('div', 'ctrulesh', sec.h));
      for (const line of sec.lines ?? []) out.push(el('div', 'ctrulesl', line));
      if (sec.table) {
        const tb = el('table', 'ctrulest');
        for (const row of sec.table) { const tr = el('tr'); row.forEach((c) => tr.append(el('td', '', String(c)))); tb.append(tr); }
        out.push(tb);
      }
    }
    rulesCard.replaceChildren(...out);
    rulesCard.style.display = '';
  }
  addEventListener('keydown', (e) => { if (e.key === 'Escape' && rulesCard.style.display !== 'none') rulesCard.style.display = 'none'; });

  const mySeat = () => (cur ? cur.seats.findIndex((s) => s?.id === myId()) : -1);
  const myTurn = () => cur?.phase === 'playing' && mySeat() >= 0 && !!cur.actors?.includes(mySeat());
  const api = {
    send: (move) => { if (cur) send({ t: 'ct_act', table: cur.table, move }); confirm = null; },
    mySeat, myTurn, deadline: () => deadline, toast, twoStep, confirming,
    /** A seat's name, with （暂离） while the game plays for them (away: dropped off, walked off, stopped answering). */
    name: (i) => (seatName(cur?.seats?.[i], i) ?? t('cards.seat.n', { n: i + 1 })) + (cur?.away?.[i] ? t('cards.away.tag') : ''),
    /** 补位: fill the empty seats with bots (on) or take them away (games whose module says bots: true). */
    bots: (on) => { if (cur) send({ t: 'ct_bot', table: cur.table, on }); },
    seats: () => cur?.seats ?? [],
    sitAt: (i) => sitDown(cur?.table, i),
  };
  /** The mounted game module for the table's game (one at a time), mounted when first needed. */
  let mounted = null;
  function game() {
    if (!cur) return null;
    const kind = shownState().kind;
    if (mounted?.kind !== kind) {
      const def = GAMES.find((g) => g.kind === kind);
      area.replaceChildren();
      mounted = def ? { kind, def, ui: def.mount(area, api) } : null;
      panel.classList.toggle('ctwide', !!def?.wide); // a game that needs the room (麻将)
    }
    return mounted;
  }

  // ---- words
  function statusText() {
    const state = shownState(), now = Date.now(), secs = (at) => Math.max(0, Math.ceil((at - now) / 1000)), g = game(), own = g?.ui.status?.(state);
    const timed = () => (deadline > now ? t('cards.status.clock', { text: own ?? '', n: secs(deadline) }) : own ?? '');
    if (state.phase === 'playing' && state.away?.[mySeat()]) return myTurn() ? t('cards.status.away', { text: timed(), away: t('cards.away.status') }) : t('cards.away.status');
    if (state.phase === 'playing') return timed();
    if (state.phase === 'over') { const text = own ?? t('cards.status.over'); return nextAt ? t('cards.status.next', { text, n: secs(nextAt) }) : text; }
    const n = state.seats.filter(Boolean).length, def = shownDef();
    if (!previewing() && nextAt) return t('cards.status.startIn', { n: secs(nextAt) });
    if (mySeat() >= 0) return t(n >= (def?.min ?? 2) ? 'cards.status.needHuman' : def?.bots ? 'cards.status.waitBots' : 'cards.status.wait');
    return n ? t('cards.status.join') : t('cards.status.empty', { game: def?.name ?? '', blurb: def?.blurb ?? '' });
  }
  function render() {
    const show = !!cur && !!me();
    panel.style.display = show ? '' : 'none';
    if (!show) return;
    if (confirm && Date.now() >= confirm.until) confirm = null;
    const seat = mySeat(), seated = seat >= 0, state = shownState(), def = GAMES.find((k) => k.kind === state.kind);
    put(title, def?.icon ?? 'cards', `${kindLabel(state.kind, state.opts, state.kindName)} · ${state.name}`);
    role.textContent = seated ? t('cards.seat.n', { n: seat + 1 }) : cur.phase === 'playing' ? t('cards.watching') : '';
    role.style.display = role.textContent ? '' : 'none';
    fold.textContent = t(HEAD.fold[folded ? 'folded' : 'open']);
    panel.classList.toggle('ctfull', full);
    fullBtn.textContent = t(HEAD.full[full ? 'full' : 'small']);
    fold.style.display = full ? 'none' : '';
    body.style.display = folded ? 'none' : '';
    const others = cur.seats.some((s) => s && s.id !== myId());
    pick.style.display = GAMES.length > 1 && cur.phase !== 'playing' && !others ? '' : 'none';
    for (const b of pick.querySelectorAll('button')) b.classList.toggle('on', b.dataset.kind === (seated ? cur.kind : pickSel ?? cur.kind));
    // options (麻将's 玩法): shown with the picker (or seated alone between games) where there's a choice
    const sd = shownDef(), so = shownOpts(), canOpt = cur.phase !== 'playing' && !others;
    optRow.replaceChildren(...(canOpt ? sd?.options ?? [] : []).filter((o) => o.choices.length > 1).flatMap((o) => [el('span', 'gmpicklabel', t('cards.opt.label', { label: o.label })),
      ...o.choices.map((c) => { const b = el('button', (so[o.key] ?? o.choices[0].id) === c.id ? 'on' : '', c.name); b.title = c.blurb ?? ''; b.onclick = () => chooseOpt(o.key, c.id); return b; })]));
    optRow.style.display = optRow.childElementCount ? '' : 'none';
    rulesBtn.style.display = sd?.rules ? '' : 'none';
    status.textContent = statusText();
    status.classList.toggle('mine', myTurn());
    // a game this page doesn't have (a newer release's): no sitting down, it couldn't play
    sitBtn.style.display = !seated && state.seats.some((s) => !s || (String(s.id).startsWith('bot:') && state.phase !== 'playing')) && GAMES.some((g) => g.kind === state.kind) ? '' : 'none';
    leave.style.display = seated ? '' : 'none';
    const warn = game()?.ui.leaveWarn?.(cur);
    leave.textContent = confirming('leave') && warn ? warn : t('cards.leave');
    if (!folded) game()?.ui.render(state);
    fit();
  }
  // 设置 → 语言: the words built once (the picker, 坐下), the open 玩法 card, then the panel (the game's part with it)
  onLang(() => {
    pickLabel.textContent = t('cards.pick.label'); sitBtn.textContent = t('cards.sit'); rulesBtn.textContent = t('cards.rules.button');
    for (const b of pick.querySelectorAll('button')) { const k = GAMES.find((g) => g.kind === b.dataset.kind); if (k) label(b, k.icon, k.name); }
    if (rulesShown && rulesCard.style.display !== 'none') showRules(rulesShown.def, rulesShown.opts);
    render();
  });
  // 全屏 on a short screen (a phone held sideways): the table drawing gets smaller until what's under it (坐下, my
  // hand, the buttons) is in view without scrolling the panel. Only ever smaller than the CSS size, never bigger, and
  // not below 120 px high (still readable: past that the panel scrolls).
  function fit() {
    const felt = area.querySelector('.ctfelt, .ctmjtable');
    if (!felt) return;
    felt.style.width = ''; felt.style.minWidth = '';
    if (!full || folded || panel.style.display === 'none') return;
    let was = Infinity, before = '';
    for (let i = 0; i < 3; i++) { // (again if the first try's rounding left a few pixels over)
      const over = panel.scrollHeight - panel.clientHeight, r = felt.getBoundingClientRect();
      // smaller didn't help (what's beside the table is the taller, sideways): back to the last size that did
      if (over >= was) { felt.style.width = before; if (!before) felt.style.minWidth = ''; return; }
      if (over <= 0 || !r.height || r.height <= 120) return;
      was = over; before = felt.style.width;
      const h = Math.max(120, r.height - over - 1), bx = felt.offsetWidth - felt.clientWidth, by = felt.offsetHeight - felt.clientHeight; // (its border: width is the inside)
      felt.style.minWidth = '0'; felt.style.width = Math.floor(((h - by) * (r.width - bx)) / (r.height - by)) + 'px';
    }
  }
  addEventListener('resize', () => { if (cur && full) fit(); });
  // and when what's in the panel changes size between renders (the log grew, an icon loaded): fit() leaves it the same
  // size it found it once settled, so this doesn't go round
  if (typeof ResizeObserver === 'function') new ResizeObserver(() => { if (cur && full) fit(); }).observe(body);
  setInterval(() => {
    const m = me();
    if (pending && (Date.now() > pending.until || !m)) pending = null;
    if (pending && m) {
      const t = tables.find((x) => x.id === pending.id), seatedHere = cur?.table === pending.id && mySeat() >= 0;
      if (seatedHere) pending = null;
      else if (t && !mover.planning() && Date.now() - (pending.tried || 0) > 1000) {
        const at = cardSeat(t, pending.seat, pending.n);
        if (Math.hypot(m.x - at.x, m.y - at.y) <= SIT_REACH) { pending.tried = Date.now(); send({ t: 'ct_sit', table: t.id, seat: pending.seat, game: pending.game, opts: pending.opts }); }
      }
    }
    if (cur && panel.style.display !== 'none' && !folded) { status.textContent = statusText(); game()?.ui.tick?.(); if (confirm && Date.now() >= confirm.until) render(); }
  }, 250);

  // ---- sitting down: walk up to the seat (the nearest free one, or the one clicked) and sit on arrival
  function sitDown(id, seat) {
    const tb = tables.find((x) => x.id === id), m = me(); if (!tb || !m) return;
    const st = cur?.table === tb.id ? cur : null, sm = summary.get(tb.id);
    // a game picked at an empty table: its seats (the office sets the table up for it as I sit)
    const empty = !(st ?? sm)?.seats?.some(Boolean);
    const game = empty && (pickSel || pickOpts) ? pickSel ?? (st ?? sm)?.kind : undefined, gopts = game && pickOpts ? pickOpts : undefined;
    const kind = game ?? st?.kind ?? sm?.kind;
    if (kind && !GAMES.some((g) => g.kind === kind)) { toast(t('cards.err.reload')); return; }
    const n = game ? GAMES.find((g) => g.kind === game).seats : st ? st.seats.length : sm?.max ?? 8;
    // a 补位 bot's seat is free between rounds (the office gives it to whoever sits)
    const taken = game ? [] : st ? st.seats.map((p) => !!p && !(String(p.id).startsWith('bot:') && st.phase !== 'playing')) : (sm?.seats ?? []).map(Boolean);
    const free = Array.from({ length: n }, (_, i) => i).filter((i) => !taken[i]);
    const s = Number.isInteger(seat) && free.includes(seat) ? seat : nearestCardSeat(tb, n, free, m.x, m.y);
    if (s < 0) { toast(t('cards.err.full')); return; }
    pending = { id: tb.id, seat: s, n, until: Date.now() + 20000, game, opts: gopts };
    const at = cardSeat(tb, s, n);
    if (Math.hypot(m.x - at.x, m.y - at.y) <= SIT_REACH) { pending.tried = Date.now(); send({ t: 'ct_sit', table: tb.id, seat: s, game, opts: gopts }); return; }
    const request = pending;
    void mover.travelTo(at.tx, at.ty, true, [at.x, at.y]).then((started) => { if (!started && pending === request) pending = null; });
  }
  /** The card table under a map click at tile (tx, ty), if any. */
  const tableAt = (tx, ty) => tables.find((x) => Math.abs(tx - x.x) <= 2 && Math.abs(ty - x.y) <= 1) ?? null;
  /** A double click on the map: on a card table, walk up and sit (or just walk up to watch if it's full). */
  function tableClick(tx, ty) {
    const tb = tableAt(tx, ty); if (!tb) return false;
    if (cur && mySeat() >= 0) { if (cur.table !== tb.id) toast(t('cards.err.elsewhere')); return true; }
    const sm = summary.get(tb.id);
    if (sm && sm.seated >= sm.max) return false;
    sitDown(tb.id);
    return true;
  }
  /** For the map's click card (public/pick.js). */
  function pickTarget(wx, wy) {
    const tb = tableAt(Math.floor(wx), Math.floor(wy)); if (!tb) return null; // (tb: t is the words)
    return {
      key: 'cardtable:' + tb.id, mark: () => ({ x: tb.x + 0.5, y: tb.y + 0.5, r: 2.4 }),
      view() {
        const sm = summary.get(tb.id), mine = cur?.table === tb.id && mySeat() >= 0, full = sm && sm.seated >= sm.max;
        const game = sm ? kindLabel(sm.kind, sm.opts, sm.kindName) : '';
        const sub = mine ? t('cards.pick.mine') : sm?.busy ? t('cards.pick.busy', { game, n: sm.seated, max: sm.max }) : sm?.seated ? t('cards.pick.waiting', { game, n: sm.seated }) : t('cards.pick.empty', { games: GAMES.map((g) => g.name).join(t('cards.listSep')) });
        const walk = () => { const at = cardSeat(tb, 0, 8); mover.travelTo(at.tx, at.ty + 1); };
        const actions = mine ? [] : full ? [{ icon: 'people', text: t('cards.pick.watch'), go: walk }] : [{ icon: 'cards', text: t('cards.pick.play'), go: () => tableClick(tb.x, tb.y) }, { icon: 'pin', text: t('cards.pick.walk'), alt: true, go: walk }];
        // 看玩法: the rules of the game it's set up for (with its options), from anywhere on the map
        const def = GAMES.find((g) => g.kind === (sm?.kind ?? GAMES[0].kind));
        if (def?.rules) actions.push({ icon: 'note', text: t('cards.rules.view'), alt: true, go: () => showRules(def, sm?.opts ?? {}) });
        return { icon: 'cards', title: tb.name || t('cards.table'), sub, actions };
      },
      tip: () => t('cards.pick.tip'),
    };
  }

  function msg(m) {
    if (m.t === 'ct_state') {
      const was = cur?.table === m.table ? cur : null;
      if (!was) { folded = false; pickSel = null; pickOpts = null; }
      else if (was.kind !== m.kind || m.phase === 'playing' || m.seats.some(Boolean)) { pickSel = null; pickOpts = null; }
      // A different person can set up the table while I am walking to a locally previewed seat. Stop the pending
      // request if their actual game or options conflict instead of repeatedly asking the office to change it.
      const someoneElse = m.seats.some((s) => s && s.id !== myId());
      if (pending?.id === m.table && pending.game && someoneElse
        && (pending.game !== m.kind || Object.entries(pending.opts ?? {}).some(([key, value]) => m.opts?.[key] !== value))) {
        pending = null; toast(t('cards.err.E_NO_SWITCH'));
      }
      if (was && (was.round !== m.round || was.actors?.join() !== m.actors?.join())) confirm = null;
      cur = m; deadline = Date.now() + (m.left || 0); nextAt = m.nextIn ? Date.now() + m.nextIn : 0;
      if (mySeat() >= 0 && m.phase === 'playing' && (!was || was.phase !== 'playing')) folded = false;
      cardsReady.then(render);
      render();
    } else if (m.t === 'ct_bye') { if (cur?.table === m.table) { cur = null; confirm = null; render(); } }
    else if (m.t === 'ct_tables') { summary.clear(); for (const t of m.list) summary.set(t.id, t); }
    else if (m.t === 'ct_err') { if (!(pending && (m.code === 'reach' || /走到/.test(m.text)))) toast(refusalText(m)); } // cards.err.<code>
  }
  /** For the map: each table's game and seats (deck/cardtables-view.js). */
  const mapTables = () => tables.map((tb) => { const sm = summary.get(tb.id); return { ...tb, ...(sm ?? {}), ...(sm ? { kindName: kindLabel(sm.kind, sm.opts, sm.kindName) } : {}) }; });
  /** Seated: the way I face (toward the felt), as the office has it. */
  function facing() {
    const s = mySeat(), t = cur && s >= 0 ? tables.find((x) => x.id === cur.table) : null, m = me();
    if (!t || !m) return null;
    const at = cardSeat(t, s, cur.seats.length);
    if (Math.hypot(m.x - at.x, m.y - at.y) < 0.35) return at.dir;
    const dx = t.x + 0.5 - (m.x + 0.5), dy = t.y + 0.5 - (m.y + 0.8);
    return Math.abs(dx) >= Math.abs(dy) ? (dx > 0 ? 1 : 3) : dy > 0 ? 0 : 2;
  }
  return { msg, tableClick, pickTarget, tables: mapTables, current: () => cur, seated: () => mySeat() >= 0, facing, near: (x, y) => tables.some((t) => cardTableDist(t, x, y) <= 5) };
}
